import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceQuoteModal } from "@/components/ServiceQuoteModal";
import { LineChart, Target, Lightbulb, Users, CheckCircle, TrendingUp, Cog, BarChart3 } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useState } from "react";
import teamWarehouse from "@/assets/team-warehouse.jpg";
import operationsTeam from "@/assets/operations-team.jpg";

const SupplyChain = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const services = [
    {
      icon: Target,
      title: "Strategic Planning",
      description: "Develop long-term logistics strategies aligned with your business objectives",
      features: ["Network design", "Capacity planning", "Growth modeling", "Risk mitigation"]
    },
    {
      icon: BarChart3,
      title: "Cost Optimization",
      description: "Identify savings opportunities across your supply chain",
      features: ["Spend analysis", "Rate benchmarking", "Mode optimization", "Consolidation strategies"]
    },
    {
      icon: Cog,
      title: "Process Improvement",
      description: "Streamline operations for greater efficiency and reliability",
      features: ["Workflow mapping", "Bottleneck analysis", "Automation opportunities", "KPI development"]
    },
    {
      icon: TrendingUp,
      title: "Performance Management",
      description: "Measure, monitor, and improve supply chain performance",
      features: ["Dashboard creation", "Vendor scorecards", "Continuous improvement", "Regular reviews"]
    }
  ];

  const caseStudies = [
    { 
      industry: "Retail", 
      challenge: "High logistics costs eating into margins",
      solution: "Consolidated shipments and optimized routing",
      result: "23% reduction in transport costs"
    },
    { 
      industry: "Manufacturing", 
      challenge: "Inventory stockouts disrupting production",
      solution: "Implemented demand forecasting and safety stock modeling",
      result: "95% service level achievement"
    },
    { 
      industry: "E-commerce", 
      challenge: "Slow delivery times affecting customer satisfaction",
      solution: "Strategic warehouse positioning and carrier mix optimization",
      result: "40% faster delivery times"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img 
          src={teamWarehouse} 
          alt="Supply Chain Consulting" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70" />
        <div className="relative z-10 text-center text-primary-foreground px-4 animate-fade-in">
          <LineChart className="h-16 w-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Supply Chain Consulting</h1>
          <p className="text-xl max-w-3xl mx-auto mb-8 opacity-90">
            Strategic logistics planning and optimization. Transform your supply chain into a competitive advantage.
          </p>
          <Button size="lg" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
            Schedule Consultation
          </Button>
        </div>
      </section>

      <main className="py-20">
        <div className="container mx-auto px-4">
          {/* Overview */}
          <section className="mb-20 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6">Optimize Your Supply Chain</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  In today's competitive landscape, an efficient supply chain is essential. Our consulting team brings decades of experience to help you reduce costs, improve service levels, and build resilience.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  From strategic network design to day-to-day operational improvements, we provide actionable insights that deliver measurable results.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">200+</div>
                    <div className="text-sm text-muted-foreground">Projects Completed</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">18%</div>
                    <div className="text-sm text-muted-foreground">Average Cost Savings</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">50+</div>
                    <div className="text-sm text-muted-foreground">Expert Consultants</div>
                  </div>
                  <div className="text-center p-4 bg-secondary rounded-lg">
                    <div className="text-3xl font-bold text-primary">30</div>
                    <div className="text-sm text-muted-foreground">Years Experience</div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={operationsTeam} 
                  alt="Consulting Team" 
                  className="rounded-xl shadow-2xl"
                />
              </div>
            </div>
          </section>

          {/* Services */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Our Consulting Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <Icon className="h-12 w-12 text-primary mb-4" />
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-muted-foreground mb-6">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 text-primary mr-2" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* Methodology */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Our Methodology</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Discover", desc: "Deep dive into your current operations, data, and challenges" },
                { step: "2", title: "Analyze", desc: "Benchmark performance and identify improvement opportunities" },
                { step: "3", title: "Design", desc: "Develop tailored solutions and implementation roadmap" },
                { step: "4", title: "Implement", desc: "Execute changes with hands-on support and monitoring" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {item.step}
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Focus Areas */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Areas of Expertise</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[
                "Network Optimization",
                "Inventory Management",
                "Transportation Strategy",
                "Warehouse Design",
                "Demand Planning",
                "Vendor Management",
                "Technology Selection",
                "Sustainability",
                "Risk Management",
                "S&OP Process",
                "Last-Mile Delivery",
                "Reverse Logistics"
              ].map((area, index) => (
                <Card key={index} className="text-center hover-scale animate-fade-in">
                  <CardContent className="p-4">
                    <Lightbulb className="h-6 w-6 text-primary mx-auto mb-2" />
                    <span className="font-medium text-sm">{area}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Case Studies */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Success Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {caseStudies.map((study, index) => (
                <Card key={index} className="hover:shadow-xl transition-all hover-scale animate-fade-in">
                  <CardContent className="p-8">
                    <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">{study.industry}</span>
                    <h3 className="text-xl font-bold mt-4 mb-3">Challenge</h3>
                    <p className="text-muted-foreground mb-4">{study.challenge}</p>
                    <h3 className="text-xl font-bold mb-3">Solution</h3>
                    <p className="text-muted-foreground mb-4">{study.solution}</p>
                    <div className="bg-secondary rounded-lg p-4">
                      <h3 className="font-bold text-primary mb-1">Result</h3>
                      <p className="font-semibold">{study.result}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Engagement Models */}
          <section className="bg-secondary rounded-xl p-12 mb-20 animate-fade-in">
            <h2 className="text-3xl font-bold text-center mb-12">Engagement Models</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  model: "Project-Based", 
                  desc: "Defined scope, timeline, and deliverables",
                  ideal: "Specific challenges or initiatives"
                },
                { 
                  model: "Retainer", 
                  desc: "Ongoing advisory and support",
                  ideal: "Continuous improvement programs"
                },
                { 
                  model: "Embedded", 
                  desc: "Full-time consultant placement",
                  ideal: "Major transformations"
                }
              ].map((engagement, index) => (
                <Card key={index}>
                  <CardContent className="p-6 text-center">
                    <Users className="h-10 w-10 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">{engagement.model}</h3>
                    <p className="text-muted-foreground mb-4">{engagement.desc}</p>
                    <p className="text-sm text-primary">Ideal for: {engagement.ideal}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="mb-20">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Our Consulting?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: Target, title: "Results-Driven", desc: "We measure success by the tangible improvements we deliver to your business" },
                { icon: Users, title: "Industry Expertise", desc: "Consultants with hands-on experience across retail, manufacturing, and e-commerce" },
                { icon: Lightbulb, title: "Practical Solutions", desc: "Actionable recommendations that can be implemented, not theoretical frameworks" }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="text-center hover-scale animate-fade-in">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                      <p className="text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="text-center bg-primary text-primary-foreground rounded-xl p-12 animate-fade-in">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Supply Chain?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Schedule a free consultation with our supply chain experts. Let's discuss how we can help you achieve your logistics goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                Schedule Free Consultation
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary" asChild>
                <NavLink to="/contact">Contact Consulting Team</NavLink>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <ServiceQuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} serviceType="supply-chain" />
    </div>
  );
};

export default SupplyChain;
